import { t } from '@lingui/core/macro';
import { useMemo } from 'react';

import { type AudienceGroupedOptionType } from '@/api/audience';
import { type PixelType } from '@/api/pixel';
import PixelImpressionTrendChart from '@/app/pixels/[pixels]/PixelImpressionTrendChart';
import PixelOpportunityInfluenceChartTable from '@/app/pixels/[pixels]/PixelOpportunityInfluenceChartTable';
import PixelTargetingEfficiencyChart from '@/app/pixels/[pixels]/PixelTargetingEfficiencyChart';
import { PageSpinner } from '@/components/loaders';
import { PageAudienceFilter, PageDateRangeFilter, usePageFilterContext } from '@/components/page';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import ShareMenu from '@/features/ShareMenu';

import PixelDetailKPIMetrics from './PixelDetailKPIMetrics';
import PixelReachAndEngagementTrendChart from './PixelReachAndEngagementTrendChart';

const GROUPED_AUDIENCE_ID = 'grouped_audience';

type Props = {
  pixelIds?: string[];
  pixels?: PixelType[];
};

const PixelDetailContent = ({ pixelIds, pixels }: Props) => {
  const { pageFilters } = usePageFilterContext();
  const isGrouped = !!(pixelIds && pixelIds?.length > 1);

  const groupedAudience = useMemo<AudienceGroupedOptionType>(() => {
    const audienceIds = [...new Set(pixels?.map((pixel) => pixel.audience?.id).filter(Boolean))];
    return {
      id: GROUPED_AUDIENCE_ID,
      name: audienceIds.length ? t`Grouped Audience (${audienceIds.length})` : t`Grouped Audience`,
      isTAM: false,
      audienceIds,
    };
  }, [pixels]);

  const audience = useMemo<AudienceGroupedOptionType | undefined>(() => {
    const aud = pageFilters.audience;

    if (aud) {
      if (aud.id === GROUPED_AUDIENCE_ID) {
        return groupedAudience;
      }

      return {
        ...aud,
        audienceIds: [aud.id],
      };
    }
  }, [pageFilters.audience, groupedAudience]);

  const prependedAudienceOptions = useMemo(
    () => (isGrouped ? [groupedAudience] : undefined),
    [isGrouped, groupedAudience],
  );

  return (
    <>
      <div className="flex justify-between">
        <PageDateRangeFilter />
        <div className="flex items-center gap-2">
          <PendoPlaceholder />
          <PageAudienceFilter
            prependedOptions={prependedAudienceOptions}
            defaultAudience={isGrouped ? groupedAudience : pixels?.[0].audience}
          />
          <ShareMenu
            notificationPageKey="pixel"
            notificationParams={{
              pixelIds: pixelIds!,
              audienceIds: audience?.id,
            }}
          />
        </div>
      </div>
      {audience ? (
        <>
          <PixelDetailKPIMetrics pixelIds={pixelIds} audience={audience} />
          <PixelReachAndEngagementTrendChart pixelIds={pixelIds} audience={audience} />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-24">
            <div className="md:col-span-10">
              <PixelImpressionTrendChart pixelIds={pixelIds} audience={audience} />
            </div>
            <div className="md:col-span-14">
              <PixelTargetingEfficiencyChart pixelIds={pixelIds} audience={audience} />
            </div>
          </div>
          <PixelOpportunityInfluenceChartTable pixelIds={pixelIds} audience={audience} />
        </>
      ) : (
        <PageSpinner />
      )}
    </>
  );
};

export default PixelDetailContent;
